import { z } from 'zod';

/**
 * No-code program · Phase 9 · Describe-mode element targeting.
 *
 * Two surfaces live in this file:
 *
 * 1. `TargetMetaSchema` — the per-step editor blob persisted alongside
 *    `step.target` so the runtime knows whether the string came from
 *    the live picker, a free-text description, or a raw paste.
 *    The runner reads this to decide which `ResolveOptions` hints to
 *    forward to `element-resolver.ts` (label / nearText / placeholder).
 *
 * 2. `ElementHintRefineRequestSchema` / `ElementHintRefineResponseSchema`
 *    — the contract for `POST /api/element-hint/refine`. The Describe
 *    form's "Refine with AI" button posts a free-text element
 *    description and gets back structured locator hints
 *    (`role` / `label` / `near` / `placeholder` + a confidence score).
 *
 * Why one file: the editor reads the meta blob AND calls the refine
 * endpoint from the same component (`DescribeElementForm.tsx`). Keeping
 * the two shapes together means a future change to the meta schema
 * only has to touch one Zod tree.
 */

// ---- TargetMeta -------------------------------------------------------

/** Authoring path the user picked when they set `step.target`. */
export const TargetModeSchema = z.enum(['pick', 'describe', 'raw']);
export type TargetMode = z.infer<typeof TargetModeSchema>;

/**
 * Pick-mode metadata captured by the live `ElementInspector` (CDP).
 * Already de-facto persisted today via `step.pickedFieldsMeta` — this
 * shape is the cleaned-up version for Phase 9 onwards. Both readers
 * (chip preview, runner) consult `targetMeta` first and fall back to
 * `pickedFieldsMeta` so old flows keep rendering identically.
 */
export const TargetMetaPickSchema = z.object({
  mode: z.literal('pick'),
  /** Strategy name reported by the picker, e.g. `'Test ID'`. */
  strategy: z.string().min(1),
  /** 0-100; 99 = data-testid, 30 = absolute XPath fallback. */
  confidence: z.number().min(0).max(100),
  /** Tag of the element clicked, e.g. `'button'`. */
  pickedTag: z.string().optional(),
  /** Visible text content (truncated). */
  pickedText: z.string().optional(),
  /** ISO timestamp of when the locator was captured. */
  pickedAt: z.string().optional(),
});
export type TargetMetaPick = z.infer<typeof TargetMetaPickSchema>;

/**
 * Describe-mode metadata. `step.target` holds the human-readable
 * description ("the email field"); this blob carries the structured
 * hints the runner forwards to the resolver. All fields optional —
 * the resolver tolerates partial hints (it just falls back to broader
 * tiers when a hint is missing).
 */
export const TargetMetaDescribeSchema = z.object({
  mode: z.literal('describe'),
  /** ARIA role hint ('textbox', 'button', 'link', 'checkbox', …). */
  role: z.string().optional(),
  /** Label/name hint (matched against `aria-label`, `<label for>`, …). */
  label: z.string().optional(),
  /** Nearby visible text used for sibling/parent disambiguation. */
  near: z.string().optional(),
  /** Placeholder text hint (input-only). */
  placeholder: z.string().optional(),
  /** True when the AI refine endpoint populated the structured fields. */
  aiRefined: z.boolean().optional(),
  /** Provider that produced the refined hints ('openai' | 'groq' | …). */
  aiProvider: z.string().optional(),
  /** ISO timestamp of when the refinement was last accepted. */
  refinedAt: z.string().optional(),
  /**
   * Phase 9.x · `defer-describe-auto-promote` — when a describe-mode
   * step PASSES a run via healing (or, post-v2, the resolver's first
   * match), the runner-side selector that succeeded is promoted onto
   * the meta blob. Subsequent runs try this selector FIRST and fall
   * back to the describe ladder if it misses (no reliability
   * regression — the healing engine still owns recovery).
   *
   * Cleared automatically whenever the user hand-edits `step.target`
   * (a new description invalidates any cached selector).
   */
  promotedSelector: z.string().optional(),
  /** ISO timestamp of when `promotedSelector` was last set. */
  promotedAt: z.string().optional(),
});
export type TargetMetaDescribe = z.infer<typeof TargetMetaDescribeSchema>;

/** Raw-paste metadata. Empty by design — the chip just needs to know
 *  to render the `</>` icon variant and skip the friendly preview. */
export const TargetMetaRawSchema = z.object({
  mode: z.literal('raw'),
});
export type TargetMetaRaw = z.infer<typeof TargetMetaRawSchema>;

export const TargetMetaSchema = z.discriminatedUnion('mode', [
  TargetMetaPickSchema,
  TargetMetaDescribeSchema,
  TargetMetaRawSchema,
]);
export type TargetMeta = z.infer<typeof TargetMetaSchema>;

// ---- AI refine endpoint -----------------------------------------------

/**
 * `stepType` is informational — it tightens the LLM's defaults
 * (a `type` step almost always wants `role: 'textbox'`, a `click`
 * almost always wants `'button' | 'link'`). Free string so we don't
 * have to import the StepType union into contracts.
 */
export const ElementHintRefineRequestSchema = z.object({
  description: z.string().min(2).max(500),
  stepType: z.string().min(1).max(64).optional(),
  /** ARIA role the editor pre-selected from `defaultRole`. The
   *  refiner is allowed to override it but treats this as a strong
   *  starting hint. */
  defaultRole: z.string().max(64).optional(),
});
export type ElementHintRefineRequest = z.infer<typeof ElementHintRefineRequestSchema>;

export const ElementHintRefineResponseSchema = z.object({
  role: z.string().optional(),
  label: z.string().optional(),
  near: z.string().optional(),
  placeholder: z.string().optional(),
  /** 0-1. Cheap signal; the runtime never gates on this — it's purely
   *  for the UI to render a "Likely / Possibly / Best guess" chip. */
  confidence: z.number().min(0).max(1),
  /** Adapter that served the response: 'openai' | 'groq' | 'anthropic'
   *  | 'rule' (deterministic fallback when no LLM is configured). */
  provider: z.string(),
  /** One-sentence rationale shown under the AI badge. */
  explanation: z.string().max(280),
  /** True when the response came from the in-process cache. */
  cached: z.boolean().optional(),
});
export type ElementHintRefineResponse = z.infer<typeof ElementHintRefineResponseSchema>;
